
import React, { useEffect, useState, useRef } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle 
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 1 hour in milliseconds
const WARNING_BEFORE_TIMEOUT = 5 * 60 * 1000; // 5 minutes before timeout

export const SessionManager: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, logout, refreshSession } = useAuth();
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const { toast } = useToast();
  
  // Use refs to store timers that need to be cleared on component unmount
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  
  // Reference to store last activity time
  const lastActivityRef = useRef(Date.now());
  
  // Reset the inactivity timer
  const resetInactivityTimer = () => {
    // Clear existing timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    
    // Update last activity timestamp
    lastActivityRef.current = Date.now();
    
    // Only set timers if user is logged in
    if (session) {
      // Set timer for warning
      warningTimerRef.current = setTimeout(() => {
        setShowTimeoutWarning(true);
        setTimeLeft(WARNING_BEFORE_TIMEOUT);
        startCountdown();
      }, INACTIVITY_TIMEOUT - WARNING_BEFORE_TIMEOUT);
      
      // Set timer for automatic logout
      inactivityTimerRef.current = setTimeout(() => {
        handleSessionExpired();
      }, INACTIVITY_TIMEOUT);
    }
  };
  
  // Start the countdown timer for the warning dialog
  const startCountdown = () => {
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    
    countdownTimerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        const newTimeLeft = prev - 1000;
        if (newTimeLeft <= 0) {
          if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
          return 0;
        }
        return newTimeLeft;
      });
    }, 1000);
  };
  
  // Handle when session expires
  const handleSessionExpired = async () => {
    setShowTimeoutWarning(false);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    
    await logout();
    
    toast({
      title: "Sesión expirada",
      description: "Tu sesión ha caducado debido a inactividad. Por favor, inicia sesión nuevamente.",
      variant: "destructive"
    });
  };
  
  // Extend the session when user clicks to stay logged in
  const handleStayLoggedIn = async () => {
    setShowTimeoutWarning(false);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    
    try {
      // Refresh the session token to extend its lifetime
      await refreshSession();
      resetInactivityTimer();
      
      toast({
        title: "Sesión extendida",
        description: "Tu sesión ha sido extendida exitosamente."
      });
    } catch (error) {
      console.error("Error extending session:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo extender la sesión. Por favor, inicia sesión nuevamente."
      });
      await handleSessionExpired();
    }
  };
  
  // Set up event listeners and initial timer
  useEffect(() => {
    if (!session) return;
    
    // User activity event listeners
    const activityEvents = [
      'mousedown', 'mousemove', 'keypress', 
      'scroll', 'touchstart', 'click'
    ];
    
    const handleUserActivity = () => {
      // Only reset timer if significant time has passed (1 min) to avoid 
      // constantly resetting on continuous activity
      if (Date.now() - lastActivityRef.current > 60000) {
        resetInactivityTimer();
      }
    };
    
    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });
    
    // Initialize timer on component mount or when session changes
    resetInactivityTimer();
    
    // Check if session is about to expire based on supabase token
    const checkTokenExpiration = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          const expiresAt = data.session.expires_at;
          if (expiresAt) {
            const expiresAtDate = new Date(expiresAt * 1000);
            const timeToExpiry = expiresAtDate.getTime() - Date.now();
            
            // If token is about to expire in less than warning time
            if (timeToExpiry > 0 && timeToExpiry < WARNING_BEFORE_TIMEOUT) {
              setShowTimeoutWarning(true);
              setTimeLeft(timeToExpiry);
              startCountdown();
            }
            
            // If token will expire within next 10 min but after warning time,
            // preemptively refresh it without showing warning
            if (timeToExpiry > WARNING_BEFORE_TIMEOUT && timeToExpiry < WARNING_BEFORE_TIMEOUT + (5 * 60 * 1000)) {
              await refreshSession();
              resetInactivityTimer();
            }
          }
        }
      } catch (err) {
        console.error("Error checking token expiration:", err);
      }
    };
    
    checkTokenExpiration();
    
    // Set up a periodic token refresh every 30 minutes for long sessions
    const tokenRefreshInterval = setInterval(async () => {
      if (session) {
        try {
          await refreshSession();
          console.log("Session token refreshed periodically");
        } catch (err) {
          console.error("Error in periodic token refresh:", err);
        }
      }
    }, 30 * 60 * 1000); // 30 minutes
    
    // Cleanup on unmount
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
      
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
      clearInterval(tokenRefreshInterval);
    };
  }, [session, logout, refreshSession]);
  
  // Format time for display
  const formatTimeLeft = () => {
    const minutes = Math.floor(timeLeft / 60000);
    const seconds = Math.floor((timeLeft % 60000) / 1000);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };
  
  return (
    <>
      {children}
      
      {/* Session Timeout Warning Dialog */}
      <Dialog open={showTimeoutWarning} onOpenChange={setShowTimeoutWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>¿Sigues ahí?</DialogTitle>
            <DialogDescription>
              Tu sesión está a punto de expirar debido a inactividad. 
              Cerraremos tu sesión en {formatTimeLeft()}.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-col sm:flex-row sm:justify-between">
            <Button 
              variant="outline" 
              onClick={handleSessionExpired}
            >
              Cerrar sesión
            </Button>
            <Button 
              onClick={handleStayLoggedIn}
              className="mt-2 sm:mt-0"
            >
              Mantener sesión activa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SessionManager;
